/* eslint-disable react/destructuring-assignment */
import { LuChevronUp, LuX } from "react-icons/lu";
import { useLazyQuery } from "@apollo/client";
import { Button, Dropdown, Form, Input, message, Tooltip } from "antd";
import { capitalize, isEmpty, isNull, isUndefined } from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_AI_RESPONSE } from "../../../graphql/query";

import { UserContext } from "../../../amplify/authenticator-provider";
import CustomButton from "../../commonComponents/button";
import { setFieldAndValidate } from "../../../library/antdHelpers";
import { DownIcon, EmailAiIcon, EmailIcon, GreyPersonIcon } from "../../../icons/js/EmailIcons";
import { applicationUseCaseMapping, getApplicationUseCaseId } from "../../../library/utils";
import { setselectedEmail, setselectedText } from "../../../redux/slices/leadSlice";
import { setEmailAddress, setEmailBody, setEmailSubject } from "../../../redux/slices/userSlice";
import { setEmailAiResponseHistoryId } from "../../../redux/slices/aiSlice";
import RichTextEditorWithAudioRecorder from "./RichTextEditorWithAudioRecorder";
import { AntdFormLabel } from "../../commonComponents/AntdFormsComponents";
import { addFilesToAttachments } from "./engagementActivity";
import RefineButton from "./aiResponse/RefineButton";
import CustomizeAiResponse from "./aiResponse/CustomizeAiResponse";
import useAiDraftWriter from "./aiResponse/useAiDraftWriter";
import { createEmailAdapter } from "./aiResponse/draftAdapters";
import useDraftPresence from "./aiResponse/useDraftPresence";
import { AI_ACTION, AI_CHANNEL, AI_RESPONSE_COPY } from "./aiResponse/constants";

/**
 *
 * @param {{textDisabled:()=>boolean,emailTextRef:any,setEmailCursor:()=>any,email:string,setemail:import("react").Dispatch<import("react").SetStateAction<string>>,emailMode:"edit"|"select",emailForm:import("antd").FormInstance,handleEmail:()=>void,className:string,showTo:boolean,showSubject:boolean,to?:string,disableToEmail?:boolean,body:string}} props
 * @returns
 */
export const EmailTextBox = props => {
  const {
    emailMode = "select",
    showTo = true,
    showSubject = true,
    disableToEmail = false,
    useFlexLayout = false,
  } = props;
  const dispatch = useDispatch();
  const { selectedLead } = useSelector(state => state.lead);
  const { selectedEmail } = useSelector(state => state.lead);
  const { selectedText } = useSelector(state => state.lead);

  const replaceSelectedText = newText => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(newText));
    }
  };
  useEffect(() => {
    if (isEmpty(props.to)) {
      const tmpSelectedEmail =
        (selectedLead?.emails || [])
          .filter(email => !isEmpty(email.email) && email.isPrimary)
          .sort((a, b) => {
            // Handle both string and numeric IDs
            const idA = a?.id;
            const idB = b?.id;

            if (isUndefined(idA) || isNull(idA)) return 1; // null/undefined values at the end
            if (isUndefined(idB) || isNull(idB)) return -1;

            // Otherwise use numeric comparison
            return idA - idB;
          })[0] || null; // Fallback to null if no match
      if (tmpSelectedEmail) {
        dispatch(setselectedEmail(tmpSelectedEmail));
        dispatch(setEmailAddress(tmpSelectedEmail.email));
      }
    } else {
      dispatch(setselectedEmail({ email: props.to }));
    }
  }, [selectedLead]);
  useEffect(() => {
    if (selectedEmail?.email) {
      props.emailForm.setFieldValue("to", selectedEmail?.email);
      props.emailTextRef.current.focus();
    }
  }, [selectedEmail?.email]);
  useEffect(() => {
    if (props.body) {
      props.emailTextRef.current.innerHTML = props.body;
    }
  }, [props.body]);
  useEffect(() => {
    props.emailForm.setFieldValue("to", props.email);
  }, [props.email]);
  const [previousLeadId, setPreviousLeadId] = useState(null);

  useEffect(() => {
    if (!isEmpty(selectedEmail)) {
      dispatch(setEmailAddress(selectedEmail.email));
    }
  }, [selectedEmail]);

  // Only reset form when lead actually changes, not on every re-render
  useEffect(() => {
    const currentLeadId = selectedLead?.id;
    if (currentLeadId !== previousLeadId && previousLeadId !== null) {
      props.emailForm.resetFields();
      setPreviousLeadId(currentLeadId);
    } else if (previousLeadId === null) {
      setPreviousLeadId(currentLeadId);
    }
  }, [selectedLead?.id, previousLeadId, props.emailForm]);
  return (
    <div className={useFlexLayout ? "pt-1 h-full flex flex-col" : "pt-1 h-full"}>
      <Form
        className={useFlexLayout ? "flex-1 flex flex-col" : ""}
        onFinish={props.handleEmail}
        form={props.emailForm}
      >
        {showTo && (
          <div className="flex-row-centered gap-1 px-1">
            <span className="text-grey-700 text-[14px] font-semibold">To:</span>
            {emailMode === "select" ? (
              <Dropdown
                menu={{
                  items: (selectedLead?.emails || [])
                    .filter(item => !isEmpty(item.email))
                    .map(mail => ({
                      label: (
                        <div
                          onClick={() => {
                            dispatch(setselectedEmail(mail));
                            dispatch(setEmailAddress(mail.email));
                          }}
                          className="flex-row-centered justify-between items-center w-full"
                        >
                          <span className="flex-row-centered gap-4">
                            <div className="flex-row-centered gap-1">
                              <GreyPersonIcon />
                              <span>{selectedLead.fullName}</span>
                            </div>
                            <span>
                              {mail.email.substring(0, 35)} ({capitalize(mail.emailType)})
                            </span>
                          </span>
                          {mail.isPrimary && (
                            <span className="xs-600 text-grey-700">Primary Email</span>
                          )}
                        </div>
                      ),
                      key: mail.id,
                    })),
                }}
                overlayClassName="w-1/3 scrollable-dropdown"
                trigger="click"
              >
                {!isEmpty(selectedEmail) ? (
                  <Tooltip title={selectedLead.fullName}>
                    <span className="border border-solid flex-row-centered cursor-pointer gap-1 rounded-[30px] p-1 px-2 sm-400 bg-grey-50 text-grey-700 border-grey-200">
                      <GreyPersonIcon /> {selectedEmail?.email} <DownIcon size="20px" />
                    </span>
                  </Tooltip>
                ) : (
                  <span className="cursor-pointer">Select Email</span>
                )}
              </Dropdown>
            ) : (
              <Form.Item
                className="email-label"
                name="to"
                rules={[
                  { required: true, message: "Please enter an email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  // value={props.email}
                  name="to"
                  disabled={disableToEmail}
                  className="w-[300px]"
                  onChange={e => {
                    // props.setemail(e.target.value);
                    dispatch(setEmailAddress(e.target.value));
                  }}
                  placeholder="Enter the email address"
                  variant="borderless"
                />
              </Form.Item>
            )}
          </div>
        )}
        <Form.Item className="hidden" name="to" />
        {showSubject && (
          <>
            {showTo && <div className="w-full mt-1 h-[1px] bg-grey-200" />}
            <Form.Item
              name="subject"
              rules={[{ required: true, message: "Please enter subject", whitespace: true }]}
              className="px-2 !mb-0"
              label={<AntdFormLabel label="Subject" />}
            >
              <Input
                disabled={props.textDisabled()}
                variant="borderless"
                name="subject"
                onChange={e => dispatch(setEmailSubject(e.target.value))}
                placeholder="Enter subject line here"
              />
            </Form.Item>
            <div className="w-full mt-1 h-[1px] bg-grey-200" />
          </>
        )}

        <div className={useFlexLayout ? "flex-1 min-h-0" : ""}>
          <RichTextEditorWithAudioRecorder
            useFlexLayout={useFlexLayout}
            editorHeight={useFlexLayout ? "" : props.className || "h-[20vh]"}
            editorCustomClassName="!border-none !p-2"
            toolBarPosition="bottom"
            propRef={props.emailTextRef}
            controllerClassName="bg-grey-50"
            setcontent={value => dispatch(setEmailBody(value))}
            textDisabled={props.textDisabled()}
            onSelect={value => {
              dispatch(setselectedText(value));
            }}
            selectedText={selectedText}
            getSelectionRange={value => {
              props.setEmailCursor(value.savedRange);
            }}
            onAttachFiles={
              props.setAttachments && !props.textDisabled()
                ? files => addFilesToAttachments(files, props.setAttachments)
                : null
            }
            FOBJSX={
              <FOBComponent
                onRefineComplete={props.onEmailRefineComplete}
                showRefine={props.showRefine !== false}
                emailTextRef={props.emailTextRef}
                replaceSelectedText={replaceSelectedText}
                selectedText={selectedText}
                emailForm={props.emailForm}
                emailMode={emailMode}
                showSubject={showSubject}
                showTo={showTo}
                selectedOppTypeId={props.selectedOppTypeId}
              />
            }
            allowRecording={false}
            showAttachImages={false}
            showAiPopup
          />
        </div>
      </Form>
    </div>
  );
};
/**
 *
 * @param {{emailTextRef:any,replaceSelectedText:any,emailForm:any}} props
 * @returns
 */
const FOBComponent = props => {
  const { user } = useContext(UserContext);
  const { selectedLead } = useSelector(state => state.lead);
  const { applicationUseCases } = useSelector(state => state.aiData);
  const dispatch = useDispatch();

  // EAI-1006 — one-click Refine, on the same mechanism as the Text composer.
  // The adapter is what makes it work here: it reads the user's own words
  // (excluding the quoted thread), and writes a result back as HTML above that
  // quote. See aiResponse/draftAdapters.
  const emailAdapter = useMemo(
    () =>
      createEmailAdapter({
        emailTextRef: props.emailTextRef,
        emailMode: props.emailMode,
        emailForm: props.emailForm,
        onSubjectChange: subject => dispatch(setEmailSubject(subject)),
      }),
    [props.emailTextRef, props.emailMode, props.emailForm, dispatch]
  );
  const emailUseCaseId = getApplicationUseCaseId(
    applicationUseCases,
    applicationUseCaseMapping.ENGAGEMENT_EMAIL_SUGGESTION
  )?.id;
  const buildResponsePayload = promptData => ({
    aiSuggestionUserInput: { ...promptData },
    companyId: user.company.id,
    applicationUseCaseId: emailUseCaseId,
    leadId: selectedLead.id,
  });
  const { runAiDraft, pendingAction } = useAiDraftWriter({
    channel: AI_CHANNEL.EMAIL,
    adapter: emailAdapter,
    buildResponsePayload,
    onDraftReplaced: props.onRefineComplete,
  });

  // Customize AI response panel — same inputs the Text composer builds in
  // InputArea's SuggestAMessageButton, but against the email use case.
  const [customizePromptData, setCustomizePromptData] = useState({
    quickTipIds: [],
    toneIds: [],
    additionalNote: "",
  });
  const { selectedOppTypeId } = props;
  const locationId =
    Number(String(sessionStorage.getItem("currentLocationId") || "").split(",")[0]) ||
    user?.location?.id ||
    undefined;
  const quickTipVariables = {
    companyId: user.company.id,
    // With an active opp-type tab, defer to BE department derivation via
    // rootOppTypeId; otherwise keep the legacy user.departmentIds path.
    locationIds:
      !selectedOppTypeId && Array.isArray(user?.departmentIds) && user.departmentIds.length > 0
        ? null
        : sessionStorage.getItem("currentLocationId")?.split(","),
    applicationUseCaseId: emailUseCaseId,
    departmentIds: selectedOppTypeId ? null : user?.departmentIds || null,
    rootOppTypeId: selectedOppTypeId || null,
  };
  // The editor is a contenteditable written to by the user AND by Otto, Use
  // Template, Insert Fields and transcription — so it is watched directly rather
  // than via any one writer's state. See useDraftPresence.
  const hasDraft = useDraftPresence(emailAdapter);

  const [getAiResponse, { loading: getAiResponseLoader }] = useLazyQuery(GET_AI_RESPONSE);
  const generateAiResponse = async () => {
    // A generate ends any refine chain — the draft those versions describe is
    // about to be replaced wholesale. The Customize panel's Generate gets this
    // from useDraftVersions.record; this one runs its own query, so it says so
    // itself. See InputArea's onEmailGenerate.
    props.onEmailGenerate?.();
    try {
      const res = await getAiResponse({
        fetchPolicy: "network-only",
        variables: {
          aiSuggestionUserInput: {
            quickTips: [],
            tones: [],
            additionalNote: "",
          },
          companyId: user.company.id,
          applicationUseCaseId: getApplicationUseCaseId(
            applicationUseCases,
            applicationUseCaseMapping.ENGAGEMENT_EMAIL_SUGGESTION
          )?.id,
          leadId: selectedLead.id,
        },
      });

      dispatch(setEmailAiResponseHistoryId(res?.data?.getAiResponse?.aiResponseHistoryId));

      if (res.data.getAiResponse.statusCode === 200) {
        const bodyContent = !isEmpty(res.data.getAiResponse.bodyHtml)
          ? res.data.getAiResponse.bodyHtml
          : !isEmpty(res.data.getAiResponse.body)
          ? res.data.getAiResponse.body
          : res.data.getAiResponse.content;
        if (props?.emailMode === "select") {
          props.emailTextRef.current.innerHTML = bodyContent;
          setFieldAndValidate(props.emailForm, "subject", res.data.getAiResponse.subject);
        } else {
          const container = document.getElementById("email-text-box-container");
          if (!container) {
            return;
          }

          const editor = container.querySelector("#rich-text");
          if (!editor) {
            return;
          }

          const replyInfo = editor.querySelector("#reply-info");
          if (!replyInfo) {
            return;
          }

          // Step 1: Find top-level block in editor that wraps replyInfo
          let preserveBlock = replyInfo;
          while (preserveBlock.parentNode && preserveBlock.parentNode !== editor) {
            preserveBlock = preserveBlock.parentNode;
          }

          // Step 2: Completely clear editor
          editor.innerHTML = "";

          // Step 3: Add preserveBlock back in
          editor.appendChild(preserveBlock);

          // Step 4: Clean up inside preserveBlock — remove anything BEFORE #reply-info
          const innerNodes = Array.from(preserveBlock.childNodes);
          let shouldRemove = true;

          innerNodes.forEach(node => {
            if (node === replyInfo) {
              shouldRemove = false;
            }
            if (shouldRemove) {
              preserveBlock.removeChild(node);
            }
          });

          // Step 5: Insert AI-generated content above preserveBlock
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = bodyContent;

          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }

          editor.insertBefore(fragment, preserveBlock);
          editor.focus();
        }
      } else {
        message.error(res.data.getAiResponse.statusMessage);
      }
    } catch (err) {
      console.log(err);
    }
  };
  return (
    <div className="border-solid border flex-row-centered cursor-pointer items-center gap-1 border-grey-200 bg-white rounded">
      {/* Same CustomButton shell as RefineButton beside it, so both show the one
          spinner, centred, in a box that keeps its size while loading. The old
          markup swapped the 22px icon for a smaller `animate-spin` circle with a
          right margin, which read as a different control mid-flight. */}
      <CustomButton
        type="text"
        // Never a submit: this sits inside the email composer's <Form>, and
        // CustomButton leaves htmlType as "" — invalid, which HTML resolves to
        // "submit". See the same note on RefineButton.
        htmlType="button"
        className="flex items-center justify-center p-0 w-[30px] h-[30px]"
        icon={<EmailAiIcon size={22} />}
        loading={getAiResponseLoader}
        tooltipTitle="Use Otto Ai to write better emails"
        tooltipPlacement="left"
        handleClick={() => {
          if (getAiResponseLoader) return;
          generateAiResponse();
        }}
      />
      <div className="h-[30px] w-[1px] bg-grey-200" />
      {/* EAI-1006 — held back on the reply/forward drawer for now; that composer
          opens on a quoted thread and the flow there still needs a look. */}
      {props.showRefine !== false && (
        <>
          <RefineButton
            channel={AI_CHANNEL.EMAIL}
            onRefine={() =>
              runAiDraft({
                action: AI_ACTION.REFINE,
                promptData: { quickTips: [], tones: [], additionalNote: "" },
              })
            }
            refining={pendingAction === AI_ACTION.REFINE}
            hasDraft={hasDraft}
          />
          <div className="h-[30px] w-[1px] bg-grey-200" />
        </>
      )}
      {/* Same Customize AI response panel as the Text composer — tips, tone,
          instructions, hide/unhide, custom tips/tones, saved defaults and the
          maximize drawer — scoped to the email use case and writing through
          the email adapter (HTML, above any quoted thread). */}
      <CustomizeAiResponse
        user={user}
        locationId={locationId}
        applicationUseCaseId={emailUseCaseId}
        quickTipVariables={quickTipVariables}
        buildResponsePayload={buildResponsePayload}
        channel={AI_CHANNEL.EMAIL}
        adapter={emailAdapter}
        value={customizePromptData}
        onChange={setCustomizePromptData}
        onDraftReplaced={props.onRefineComplete}
        renderTrigger={({ open }) => (
          <Tooltip title={AI_RESPONSE_COPY.CUSTOMIZE_TOOLTIP}>
            <span className="p-1 flex-row-centered cursor-pointer">
              <LuChevronUp
                className={`text-[#64748B] text-[18px] ${
                  open ? "rotate-0" : "rotate-180"
                } transition duration-300`}
              />
            </span>
          </Tooltip>
        )}
      />
    </div>
  );
};
export const GenerateEmailJSX = props => {
  const text =
    "Hi [Customer's Name], \n I'm [Your Name], a Sales Professional at [Dealership Name]. I'd love to help you find the perfect car and make the process simple and hassle-free. \n\n Let me know if there's a specific vehicle you're interested in or if you'd like recommendations. Feel free to reply or call me at [Your Phone Number]. \n\nLooking forward to assisting you! \n\nBest regards, \nSuman Poluri \n(210) 569-2034";
  const close = () => props.setshowGenerateEmail({ open: false, type: "" });
  return (
    <div className="flex-col-centered w-[35vw]">
      <div className="flex-row-centered justify-between items-center px-4 py-3">
        <div className="flex-row-centered gap-4">
          <EmailIcon />
          <span className="sm-600 text-grey-700">Generate email</span>
        </div>
        <LuX className="text-[16px] cursor-pointer" onClick={close} />
      </div>
      <div className="h-[1px] bg-grey-200 w-full mt-1" />
      <div className="px-4 py-3">
        <span dangerouslySetInnerHTML={{ __html: text }} className="whitespace-pre-line" />
      </div>
      <div className="flex-row-centered items-center gap-2 p-2">
        <Button
          onClick={() => {
            props.emailTextRef.current.innerText = `${text}\n${props.emailTextRef.current.innerText}`;
            close();
          }}
          type="primary"
        >
          Apply
        </Button>
        <Button onClick={close}>Cancel</Button>
      </div>
    </div>
  );
};
export const GenerateSubjectJSX = props => {
  const text = "Test email subject";
  const close = () => props.setshowGenerateEmail({ open: false, type: "" });
  return (
    <div className="flex-col-centered w-[35vw]">
      <div className="flex-row-centered justify-between items-center px-4 py-3">
        <div className="flex-row-centered gap-4">
          <EmailIcon />
          <span className="sm-600 text-grey-700">Generate subject</span>
        </div>
        <LuX className="text-[16px] cursor-pointer" onClick={close} />
      </div>
      <div className="h-[1px] bg-grey-200 w-full mt-1" />
      <div className="px-4 py-3">
        <span dangerouslySetInnerHTML={{ __html: text }} className="whitespace-pre-line" />
      </div>
      <div className="flex-row-centered items-center gap-2 p-2">
        <Button
          onClick={() => {
            // props.emailTextRef.current.innerText = text;
            setFieldAndValidate(props.emailForm, "subject", text);
            close();
          }}
          type="primary"
        >
          Apply
        </Button>
        <Button onClick={close}>Cancel</Button>
      </div>
    </div>
  );
};
